import { Chart } from "chart.js/auto"; // TODO: only import the parts we need

import * as api from "../scripts/api";
import { loading } from "../scripts/loading-spinner";
import { bindData } from "../scripts/binding";

// State
let currentScore = 0;
let renderedScore = 0;
let initialScore = 0;

// DOM elements
const scoreDisplay = document.getElementById("score")!;
const chartCanvas = document.getElementById(
  "weekly-score-chart",
) as HTMLCanvasElement;

const scoreChart = new Chart(chartCanvas, {
  type: "bar",
  data: {
    labels: [] as string[],
    datasets: [{
      label: "Score",
      data: [] as number[],
      borderWidth: 0,
      barPercentage: 1,
      categoryPercentage: 1,
    }],
  },
  options: {
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        suggestedMax: 100,
        title: {
          display: true,
          text: "Total Week Score",
          color: "#b5b5b5",
        },
        grid: {
          color: "#333",
        },
      },
      y: {
        grid: {
          color: "#333",
        },
      },
    },
    // Disable the legend
    plugins: {
      legend: {
        display: false,
      },
    },
  },
});

export const loadScores = async () => {
  bindData({ "streak-icons": loading.outerHTML });
  // Join our streak and score requests so we can use the same loading spinner
  return Promise.all([
    api.getWeeklyScores(),
    api.getStreak(),
  ]).then(([scores, streak]) => {
    renderStreak(streak);
    processScores(scores);
  });
};

// Increment the score display by the given amount
export const addScore = (score: number) => {
  currentScore += score;
  scoreChart.data.datasets[0].data[0] = currentScore;
  scoreChart.update();
  renderScore();
  return currentScore;
};

export const getInitialScore = () => initialScore;

function processScores(scores: { week: string; score: number }[]) {
  initialScore = scores.reduce((acc, s) => acc + s.score, 0);
  currentScore = scores[0]?.score || 0;
  renderScore();

  // Generate all the weeks between the first and last scores so we can fill in
  // any gaps
  const firstWeek = new Date(scores[0]?.week || new Date());
  const earliestWeek = new Date(scores.at(-1)?.week || new Date());
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - 50);
  const lastWeek = new Date(
    Math.min(earliestWeek.getTime(), defaultStart.getTime()),
  );

  // Loop backwards from the last week to the first week, accounting for
  // DST changes.
  for (let d = firstWeek; lastWeek <= d; d.setUTCDate(d.getDate() - 7)) {
    const score = scores.find((s) => new Date(s.week).getTime() == d.getTime());

    scoreChart.data.labels?.push(
      d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    );
    scoreChart.data.datasets[0].data.push(score?.score || 0);
  }
  scoreChart.update();
}

function renderScore() {
  const additionalScore = currentScore - renderedScore;
  let increment = additionalScore / 60; // 1 second of animation @ 60 frames per second
  scoreDisplay.classList.add("animate-increment");

  function update() {
    renderedScore = Math.min(currentScore, renderedScore + increment);
    scoreDisplay.innerHTML = `${Math.floor(renderedScore).toLocaleString()}`;

    if (renderedScore < currentScore) {
      requestAnimationFrame(update);
    } else {
      scoreDisplay.classList.remove("animate-increment");
    }
  }

  update();
}

// Work out which coloured icons to display for the streak.
// The combined value of the icons should equal the streak
// value. The icons are:
// - 1: bronze lightning
// - 50: silver lightning
// - 20: gold lightning
function renderStreak(streak: number) {
  let icons = "";
  let streakRemaining = streak;
  while (streakRemaining > 0) {
    if (streakRemaining >= 20) {
      // gold
      icons +=
        '<span class="icon is-small streak" style="color: #f1c40f"><i class="bi bi-lightning-fill"></i></span>';
      streakRemaining -= 20;
    } else if (streakRemaining >= 5) {
      // silver
      icons +=
        '<span class="icon is-small streak" style="color: #95a5a6"><i class="bi bi-lightning-fill"></i></span>';
      streakRemaining -= 5;
    } else {
      // bronze
      icons +=
        '<span class="icon is-small streak" style="color: #cd7f32"><i class="bi bi-lightning-fill"></i></span>';
      streakRemaining -= 1;
    }
  }
  // Use a hollow icons if there's no streak
  if (!streak) icons += '<i class="bi bi-lightning"></i>';

  const streakDesc = streak == 1 ? "week" : "weeks";
  bindData({ "streak-icons": icons, "streak-desc": `${streak} ${streakDesc}` });
}
